import type { QueryIntent } from './query-ownership';

export const SEO_CTA_EXPERIMENT_ID = 'seo_cta_v1';
export const SEO_CTA_COOKIE_NAME = SEO_CTA_EXPERIMENT_ID;
export const SEO_CTA_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

export type SeoCtaVariant = 'A' | 'B';

export interface SeoCtaAssignment {
  experimentId: typeof SEO_CTA_EXPERIMENT_ID;
  variant: SeoCtaVariant;
}

export interface SeoCtaEvaluationInput {
  sessionsA: number;
  sessionsB: number;
  registrations: number;
}

const EVALUATE_PREFIXES = ['/ai-voice-receptionist', '/best/', '/industries/'] as const;
const DISCOVER_PREFIXES = ['/blog', '/reports/', '/resources/', '/guides/'] as const;

export function classifySeoCtaPath(pathname: string): QueryIntent | null {
  const path = pathname.split('?')[0]?.replace(/\/+$/, '') || '/';
  if (
    path === '/pricing' || path.startsWith('/auth/') || path.startsWith('/dashboard') ||
    path.startsWith('/onboarding')
  ) return 'convert';
  if (EVALUATE_PREFIXES.some((prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix))) {
    return 'evaluate';
  }
  if (DISCOVER_PREFIXES.some((prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix))) {
    return 'discover';
  }
  return null;
}

export function isEligibleSeoCtaPath(pathname: string): boolean {
  const intent = classifySeoCtaPath(pathname);
  return intent === 'discover' || intent === 'evaluate';
}

export function sanitizeSeoCtaAssignment(value: unknown): SeoCtaAssignment | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const experimentId = candidate.experimentId ?? candidate.experiment_id;
  const variant = candidate.variant ?? candidate.experiment_variant;
  if (experimentId !== SEO_CTA_EXPERIMENT_ID || (variant !== 'A' && variant !== 'B')) return null;
  return { experimentId: SEO_CTA_EXPERIMENT_ID, variant };
}

export function assignmentFromCookieValue(value: string | undefined | null): SeoCtaAssignment | null {
  if (value !== 'A' && value !== 'B') return null;
  return { experimentId: SEO_CTA_EXPERIMENT_ID, variant: value };
}

export function loadSeoCtaAssignment(cookieString?: string): SeoCtaAssignment | null {
  if (typeof document === 'undefined' && cookieString === undefined) return null;
  const source = cookieString ?? document.cookie;
  const encodedName = encodeURIComponent(SEO_CTA_COOKIE_NAME);
  const raw = source.split(';').map((part) => part.trim())
    .find((part) => part.startsWith(`${encodedName}=`))?.slice(encodedName.length + 1);
  return assignmentFromCookieValue(raw ? decodeURIComponent(raw) : null);
}

export function chooseSeoCtaVariant(randomValue = Math.random()): SeoCtaVariant {
  return randomValue < 0.5 ? 'A' : 'B';
}

export function seoCtaEventProperties(assignment: SeoCtaAssignment | null): Record<string, string> {
  return assignment ? {
    experiment_id: assignment.experimentId,
    experiment_variant: assignment.variant,
  } : {};
}

export function shouldActivateSeoCtaExperiment(eligibleSessions28d: number): boolean {
  return Number.isFinite(eligibleSessions28d) && eligibleSessions28d >= 200;
}

export function canEvaluateSeoCtaExperiment(input: SeoCtaEvaluationInput): boolean {
  return input.sessionsA >= 200 && input.sessionsB >= 200 && input.registrations >= 20;
}
